import {
  Component,
  EventEmitter,
  Input,
  NgModule,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import {
  DxCheckBoxModule,
  DxFormComponent,
  DxFormModule,
  DxTagBoxModule,
  DxSelectBoxModule,
  DxDataGridModule,
  DevExtremeModule,
} from "devextreme-angular";
import { formatMessage } from "devextreme/localization";
import { CommonModule } from "@angular/common";
import { OptionFormData } from "../../../model/calculation/optionFormData";
import {
  PreislistenKatalog,
  Produkt,
} from "../../../model/calculation/produkt";
import notify from "devextreme/ui/notify";
import { P3AuthService } from "../../../services";
import { User } from "../../../model/user/user";
import {
  dropOptionInvoiceCategoryUs,
  dropOptionPositionDe,
  dropOptionPositionEn,
  dropOptionTypesDe,
  dropOptionTypesEn,
  dropOptionTypesUs,
  glCodes,
  dropOptionRequiredQuantityType,
  dropOptionQuantityType,
  dropOptionCostsPer,
} from "./optionSelectBoxValues";
import { OptionUnitAdmin } from "../../../model/calculation/optionUnit";
import { environment } from "src/environments/environment";
import { OptionDataGridDeModule } from "../data-grids/option-data-grid-de/option-data-grid-de.component";
import { OptionDataGridCartonsModule } from "../data-grids/option-data-grid-cartons/option-data-grid-cartons.component";
import { OptionDataGridInsertsModule } from "../data-grids/option-data-grid-inserts/option-data-grid-inserts.component";
import { OptionDataGridOTPModule } from "../data-grids/option-data-grid-otp/option-data-grid-otp.component";
import { OptionDataGridSurchargeModule } from "../data-grids/option-data-grid-surcharge/option-data-grid-surcharge.component";
import { DxiItemComponent } from "devextreme-angular/ui/nested";
import { Option } from "../../../model/calculation/option";
import { OptionDataGridDifferenceModule } from "../data-grids/option-data-grid-difference/option-data-grid-difference.component";
import { OptionDataGridMatrixModule } from "../data-grids/option-data-grid-matrix/option-data-grid-matrix.component";
import {
  OptionFormUiElementsEditorModule,
} from "../option-form-ui-elements-editor/option-form-ui-elements-editor.component";

@Component({
  selector: "app-option-form",
  templateUrl: "./option-form.component.html",
  styleUrls: ["./option-form.component.scss"],
})
export class OptionFormComponent implements OnInit {
  @ViewChild(DxFormComponent) form: DxFormComponent;
  @ViewChild("ancillaryCode") ancillaryCodeTextBox: DxiItemComponent;

  formatMessage = formatMessage;

  @Input() optionData: OptionFormData;
  @Input() produkte: Produkt[];
  @Input() optionUnitsAdmin: OptionUnitAdmin[];
  @Input() preisListe: PreislistenKatalog;
  @Input() dependentOptions: Option[];
  @Output() optionDataChange = new EventEmitter<OptionFormData>();

  tmpLocale: string;

  optionTypesDatasource;
  optionPositionDatasource;
  optionGLCodeDatasource;
  optionRequiredQuantityType;
  optionQuantityForDatasource;
  optionCostsPer;

  optionInvoiceCategoryDatasource = dropOptionInvoiceCategoryUs;
  environmentIsUsa = environment.isUsa;

  dependentOptionsDataSource: Option[];

  isOptionTypeReadOnly: boolean;

  user: User | undefined = {
    id: "",
    identity: {
      sub: "",
      loginName: "",
      firstName: "",
      lastName: "",
      email: "",
      isInternal: false,
      enabled: false,
      locale: "",
      userCreateDate: new Date(),
      userLastModifiedDate: new Date(),
    },
    role: "",
    worksFor: [],
    notification: false,
  };

  constructor(private authService: P3AuthService) {
    this.translateLocaleValue = this.translateLocaleValue.bind(this);
    this.stayFocused = this.stayFocused.bind(this);
    this.checkOptionUnit = this.checkOptionUnit.bind(this);
    this.checkFixedValue = this.checkFixedValue.bind(this);
    this.checkOptionPosition = this.checkOptionPosition.bind(this);
    this.isReadOnly = this.isReadOnly.bind(this);
  }

  ngOnInit(): void {
    this.loadSelectBoxes();
  }

  translateLocaleValue(data): string {
    return formatMessage(`locale.${data}`);
  }

  resetValues(): void {
    this.form.instance.resetValues();
  }

  isValid(): boolean | undefined {
    return this.form.instance.validate().isValid;
  }

  stayFocused(e): void {
    setTimeout(() => {
      e.component.focus();
    });
  }

  getLocale() {
    const locale = this.user?.identity.locale;
    return locale != null ? locale : navigator.language;
  }

  loadSelectBoxes(): void {
    this.authService.getUser().subscribe(
      (e) => {
        if (e) {
          this.user = e;
        }
        this.tmpLocale = this.getLocale();
        if (this.environmentIsUsa) {
          this.optionTypesDatasource = dropOptionTypesUs;
          this.optionRequiredQuantityType = dropOptionRequiredQuantityType;
          this.optionQuantityForDatasource = dropOptionQuantityType;
          this.optionCostsPer = dropOptionCostsPer;
        } else {
          if (this.tmpLocale == "de-DE") {
            this.optionTypesDatasource = dropOptionTypesDe;
            this.optionPositionDatasource = dropOptionPositionDe;
          } else {
            this.optionTypesDatasource = dropOptionTypesEn;
            this.optionPositionDatasource = dropOptionPositionEn;
          }
        }
      },
      (e) => {
        notify({
          message: e,
          type: "error",
          displayTime: 1500,
          height: 100,
        });
      }
    );
  }

  isOptionPositionVisible(): boolean {
    return (
      this.optionData.optionType === "MEHRPREIS" ||
      this.optionData.optionType === "STAFFELMEHRPREIS" ||
      this.optionData.optionType === "STUECKMEHRPREIS"
    );
  }

  isOptionUnitVisible(): boolean {
    return (
      this.optionData.optionType === "STAFFELMEHRPREIS" ||
      this.optionData.optionType === "STUECKMEHRPREIS"
    );
  }

  isFixedValueVisible(): boolean {
    return (
      this.optionData.optionType === "AUFSCHLAG_TECHNISCHE_KOSTEN_FESTWERT" ||
      this.optionData.optionType === "AUFSCHLAG_PAPIERKOSTEN_FESTWERT" ||
      this.optionData.optionType ===
        "AUFSCHLAG_STANDARDPRODUKTKOSTEN_FESTWERT" ||
      this.optionData.optionType ===
        "AUFSCHLAG_GESAMT_NUR_TK_INKL_EXTRAS_FESTWERT" ||
      this.optionData.optionType ===
        "AUFSCHLAG_GESAMTPREIS_INKL_EXTRAS_FESTWERT"
    );
  }

  isOptionPriceInformationGridVisible(): boolean {
    return (
      this.optionData.optionType === "MEHRPREIS" ||
      this.optionData.optionType === "STAFFELMEHRPREIS" ||
      this.optionData.optionType === "STUECKMEHRPREIS"
    );
  }

  checkOptionPosition(e): boolean {
    return !(this.isOptionPositionVisible() && (!e.value || e.value === ""));
  }

  checkOptionUnit(e): boolean {
    return !(this.isOptionUnitVisible() && (!e.value || e.value === ""));
  }

  checkFixedValue(e): boolean {
    return !(this.isFixedValueVisible() && (!e.value || e.value === ""));
  }

  isOptionTypeCartonsSelected(): boolean {
    return this.optionData.optionType === "CARTONS";
  }
  isOptionTypeMatrixSelected(): boolean {
    return this.optionData.optionType === "MATRIX";
  }
  isOptionTypeDifferenceSelected(): boolean {
    return this.optionData.optionType === "DIFFERENCE";
  }
  isOptionInsertsGridVisible(): boolean {
    return this.optionData.optionType === "INSERTS";
  }
  isOptionOneTimePriceSelected(): boolean {
    return this.optionData.optionType === "ONE_TIME_PRICES";
  }
  isMatrixGridVisible() {
    return this.isOptionTypeMatrixSelected();
  }

  isOptionSurchargeAndSubSurchargeGridVisible(): boolean {
    return (
      this.optionData.optionType === "SURCHARGE" ||
      this.optionData.optionType === "SUB_SURCHARGE"
    );
  }

  onFormFieldDataChange() {
    this.isReadOnly();
    this.setOptionGLCodeDatasource();
  }

  setOptionGLCodeDatasource() {
    if (glCodes.find((e) => e.priceList == this.preisListe.name)) {
      this.optionGLCodeDatasource = glCodes.find(
        (e) => e.priceList == this.preisListe.name
      )?.codes;
    } else {
      this.optionGLCodeDatasource = glCodes.find(
        (e) => e.priceList == "DEFAULT"
      )?.codes;
    }
  }

  isReadOnly() {
    if (this.environmentIsUsa)
      this.isOptionTypeReadOnly = !!this.form.instance.option("formData").id;
  }

  checkQuantityTypeConflict(): boolean {
    if (this.isOptionTypeCartonsSelected()) {
      return true;
    }

    // Ensure both are not ORDER_QUANTITY
    return !(
      this.optionData.requiredQuantityType === "ORDER_QUANTITY" &&
      this.optionData.quantityType === "ORDER_QUANTITY"
    );
  }
}

@NgModule({
  imports: [
    DxFormModule,
    CommonModule,
    DxCheckBoxModule,
    DxTagBoxModule,
    DxSelectBoxModule,
    DxDataGridModule,
    DevExtremeModule,
    OptionDataGridCartonsModule,
    OptionDataGridDifferenceModule,
    OptionDataGridDeModule,
    OptionDataGridOTPModule,
    OptionDataGridInsertsModule,
    OptionDataGridSurchargeModule,
    OptionDataGridMatrixModule,
    OptionFormUiElementsEditorModule,
  ],
  declarations: [OptionFormComponent],
  exports: [OptionFormComponent],
})
export class OptionFormModule {}
